<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { buildChartOptions } from '../../service/chart/buildChartOptions'
import { chartLoadingShape } from '../../service/chart/chartLoadingShape'

import type { ChartRendererProps } from './types'

import { markChart } from '@/shared/lib/perf/perfMarks'
import { ChartRoot } from '@/shared/ui/chart'

const props = withDefaults(defineProps<ChartRendererProps>(), {
  state: 'ready',
  immutable: false,
  refreshing: false,
})

const emit = defineEmits<{ retry: [] }>()
// The per-type dispatch lives entirely in the builder. A gated chart arrives as `spec: null`, which
// resolves to the same null option set the builders return for a spec they cannot faithfully
// encode; ChartRoot owns both as its empty surface.
const options = computed(() => (props.spec === null ? null : buildChartOptions(props.spec)))

// Time-to-chart (ORP-22839) — no-op unless the switch is on. This is the report chart's ONE mount
// (contract 08 §2), so it is the only place the mark can go without double-counting.
onMounted(() => {
  if (props.spec !== null) markChart()
})
const loadingShape = computed(() => props.loadingShape ?? chartLoadingShape(props.spec?.type))
const optionalLabels = computed(() => ({
  ...(props.loadErrorMessage === undefined ? {} : { loadErrorMessage: props.loadErrorMessage }),
  ...(props.retryLabel === undefined ? {} : { retryLabel: props.retryLabel }),
}))
</script>

<template>
  <ChartRoot
    :options="options"
    :state="state"
    :loading-shape="loadingShape"
    :immutable="immutable"
    :refreshing="refreshing"
    :menu-items="menuItems ?? []"
    v-bind="optionalLabels"
    @retry="emit('retry')"
  />
</template>
